import type { PreviewProduct } from './types';

export type PapAddBlocker =
  | 'import_status_unavailable' | 'clash_status_unavailable'
  | 'sku_clash'
  | 'fully_imported'
  | 'variants_truncated'
  | 'no_importable_skus';

/** One client-side affordance rule for row actions, row selection and select-all.
 * The server repeats the authoritative checks immediately before every write. */
export function getPapAddBlocker(
  product: Pick<PreviewProduct, 'variantCount' | 'syncedVariantCount' | 'variantsTruncated' | 'papSkuClash' | 'papActionableVariantCount'>,
  status: { importStatusUnavailable: boolean; clashStatusUnavailable: boolean },
): PapAddBlocker | null {
  // Distinct causes: one row saying "SKU clash status is unavailable" when it was
  // actually the IMPORT lookup that degraded misreports the reason on every row.
  if (status.importStatusUnavailable) return 'import_status_unavailable';
  if (status.clashStatusUnavailable) return 'clash_status_unavailable';
  if (product.papSkuClash) return 'sku_clash';
  if (product.variantCount > 0 && product.syncedVariantCount >= product.variantCount) return 'fully_imported';
  if (product.variantsTruncated) return 'variants_truncated';
  if (product.papActionableVariantCount === 0) return 'no_importable_skus';
  return null;
}

export function isPapAddEligible(
  product: Parameters<typeof getPapAddBlocker>[0],
  status: Parameters<typeof getPapAddBlocker>[1],
): boolean {
  return getPapAddBlocker(product, status) === null;
}

/**
 * A cleared replacement-SKU input yields `''`, not `undefined`, so a
 * `edits[id] ?? suggested` fallback never fires and the blank reaches the server
 * as a Zod `.min(1)` failure surfaced in a toast. Repo convention
 * (`feedback_zod_empty_string_vs_optional`) is to disable submit instead.
 */
export function papResolvedSku(edited: string | undefined, suggested: string): string {
  const trimmed = (edited ?? '').trim();
  return trimmed.length > 0 ? trimmed : suggested;
}

export function papSkuEditsBlank(
  edits: Record<string, string>,
  conflicts: ReadonlyArray<{ variantId: string }>,
): boolean {
  return conflicts.some((conflict) => {
    const value = edits[conflict.variantId];
    return value !== undefined && value.trim().length === 0;
  });
}
